<template>
    <div class="modal">
        <h3 class="modal-title">{{ $t('ACTIVE_APPS_LIMIT_REACHED') }}</h3>

        <p class="instruction-text">{{ $t('ACTIVE_APPS_NOTICE') }}</p>

        <div class="active-apps-list" ref="appsList">
            <div v-for="app in apps" :key="app.installation_history_uuid" class="app-row" :class="{
                'marked-for-removal': appsToRemove.includes(app.installation_history_uuid)
            }" @touchstart="handleTouchStart" @touchmove="handleTouchMove" @touchend="handleTouchEnd($event, app)"
                @click="handleClick($event, app)">
                <div class="app-info">
                    <img :src="app.icon_uri" :alt="app.name" class="app-icon">
                    <span class="app-name">{{ app.name }}</span>
                </div>
                <div class="status-container">
                </div>
                <div class="app-actions">
                    <button class="remove-button">
                        {{ $t('NOT_USING') }}
                    </button>
                </div>
            </div>
        </div>

        <p class="instruction-text">{{ $t('ACTIVE_APPS_NOTICE_FOOTER') }}</p>

        <div class="modal-footer">
            <button class="g-button" @click="cancel">{{ $t('CANCEL') }}</button>
            <button class="g-button g-button_primary" @click="proceed">{{ $t('CONTINUE') }}</button>
        </div>
    </div>
</template>

<script>
export default {
  props: {
    apps: {
      type: Array,
      default: () => [],
    },
    originalError: Object,
    installOptions: Object,
    doInstall: Function,
    resolve: Function,
    reject: Function,
    handleCancel: Function,
  },
  data() {
    return {
      appsToRemove: [],
      touchStartY: 0,
      touchStartX: 0,
      touchStartTime: 0,
      isDragging: false,
    };
  },
  methods: {
    handleTouchStart(e) {
      this.touchStartY = e.touches[0].clientY;
      this.touchStartX = e.touches[0].clientX;
      this.touchStartTime = Date.now();
      this.isDragging = false;
    },
    handleTouchMove(e) {
      if (!e.touches[0]) return;

      const touchY = e.touches[0].clientY;
      const touchX = e.touches[0].clientX;
      const deltaY = Math.abs(touchY - this.touchStartY);
      const deltaX = Math.abs(touchX - this.touchStartX);

      // If moved more than 5px vertically, consider it a scroll
      if (deltaY > 5) {
        this.isDragging = true;
      }
    },
    handleTouchEnd(e, app) {
      const touchDuration = Date.now() - this.touchStartTime;

      // Only trigger toggle if:
      // 1. Not dragging (moved less than 5px)
      // 2. Touch duration is less than 200ms (quick tap)
      if (!this.isDragging && touchDuration < 200) {
        // Prevent the click event from firing
        e.preventDefault();
        e.stopPropagation();
        this.toggleRemove(app);
      }

      // Reset after a short delay
      setTimeout(() => {
        this.isDragging = false;
      }, 50);
    },
    handleClick(e, app) {
      // For non-touch devices (desktop)
      // Only process if it wasn't a touch event (prevents double firing)
      if (e.type === 'click' && !e.sourceCapabilities?.firesTouchEvents) {
        this.toggleRemove(app);
      }
    },
    toggleRemove(app) {
      const index = this.appsToRemove.indexOf(app.installation_history_uuid);
      if (index === -1) {
        this.appsToRemove.push(app.installation_history_uuid);
      } else {
        this.appsToRemove.splice(index, 1);
      }
    },
    cancel() {
      console.log('[DEBUG] Cancel button clicked');
      this.$modal.hideAll();
      console.log('[DEBUG] Modal hide requested');

      // Call handleCancel to mark operation as cancelled
      if (typeof this.handleCancel === 'function') {
        console.log('[DEBUG] Calling handleCancel function');
        this.handleCancel();
      }
    },
    async proceed() {
      console.log('[DEBUG] Proceed button clicked');

      try {
        // Check if the user has marked apps for removal
        if (this.appsToRemove.length === 0) {
          console.log('[DEBUG] User clicked continue without marking any apps - keeping modal open');
          // Show notification but keep the modal open
          this.$notify({
            type: 'info',
            text: this.$t('PLEASE_SELECT_APP_OR_MARK'),
            duration: 3000,
          });
          // Do not hide the modal if no apps were marked - keep it open
          return;
        }

        // User has marked apps for removal, now we can hide the modal
        this.$modal.hideAll();
        console.log('[DEBUG] Modal hide requested for proceed');

        // Remove the marked apps
        console.log('[DEBUG] Removing apps:', this.appsToRemove);
        await this.$api.call('set_apps_removed', {
          installation_history_uuids: this.appsToRemove,
        });

        // Retry installation without any additional options
        console.log('[DEBUG] Continuing with installation after removing apps');
        const result = await this.doInstall({});
        console.log('[DEBUG] Installation result after proceed:', result);
        this.resolve(result);
      } catch (error) {
        console.error('[DEBUG] Error in proceed method:', error);
        if (typeof this.reject === 'function') {
          this.reject(error);
        } else {
          console.error('[DEBUG] reject is not a function:', this.reject);
          // Simply re-throw the error since we can't reject
          throw error;
        }
      }
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/variables.scss";

.vm--overlay {
    background: rgba(0, 0, 0, var(--modal-overlay-opacity));
}

.active-apps-modal {
    background-color: var(--bg);
    border: 2px solid var(--lighter-primary);
    max-width: 600px;
    margin: 0 auto;
}
</style>

<style lang="scss" scoped>
@import "@/assets/variables.scss";

.modal-title {
    margin-top: 0;
    margin-bottom: 15px;
    padding: 15px 15px 0;
    font-size: 16px;
    font-weight: normal;
    color: var(--text-primary);
}

.instruction-text {
    margin-bottom: 15px;
    padding: 0 15px;
    font-size: 14px;
    line-height: 1.5;
    color: var(--text-secondary);
    text-align: left;

    &:last-of-type {
        padding: 0 15px 10px;
    }
}

.active-apps-list {
    padding: 0 15px;
    max-height: 60vh;
    overflow-y: auto;
    overflow-x: hidden;
    -webkit-overflow-scrolling: touch;

    @media (max-height: 600px) {
        max-height: 40vh;
    }

    @media (max-height: 500px) {
        max-height: 30vh;
    }
}

.app-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px;
    margin: 5px 0;
    border-radius: 4px;
    background-color: var(--secondary-bg);
    color: var(--text-primary);
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
    user-select: none;

    // Only apply hover and transition on non-touch devices
    @media (hover: hover) and (pointer: fine) {
        transition: background-color 0.2s;

        &:hover {
            background-color: var(--hover-bg);
        }

        &.marked-for-removal:hover {
            background-color: rgba(33, 150, 243, 0.3);
        }
    }

    &.marked-for-removal {
        background-color: rgba(33, 150, 243, 0.2);
        border: 1px solid #2196f3;

        .app-name {
            color: var(--text-primary);
            font-weight: 600;
        }

        .remove-button {
            color: var(--text-primary);
            font-weight: 500;
        }
    }
}

.app-info {
    display: flex;
    align-items: center;
    min-width: 150px;
    flex: 1 1 auto;
    overflow: hidden;
    margin-right: 10px;
}

.status-container {
    flex: 0 1 auto;
    text-align: center;
    padding: 0 10px;
    display: flex;
    justify-content: flex-start;
    white-space: nowrap;
}

.app-icon {
    width: 40px;
    height: 40px;
    border-radius: 8px;
    margin-right: 10px;
}

.app-name {
    font-weight: 500;
    color: var(--text-primary);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 150px;
}

.status-text {
    font-size: 14px;
    font-style: italic;

    &.status-forget {
        color: var(--text-error, #c62828);
    }
}

.app-actions {
    display: flex;
    justify-content: flex-end;
    min-width: 100px;
    flex: 0 0 auto;
}

.remove-button {
    padding: 6px 12px;
    border-radius: 4px;
    font-size: 14px;
    border: 1px solid var(--tertiary-accent);
    background-color: var(--tertiary-bg);
    color: var(--text-secondary);
    min-width: 90px;
    text-align: center;
    pointer-events: none;
}

.modal-footer {
    display: flex;
    justify-content: flex-end;
    padding: 10px 15px 15px;
    border-top: 1px solid var(--tertiary-accent);

    button:last-child {
        margin-left: 10px;
    }
}
</style>
